import { Check } from 'lucide-react';
import type { Achievement } from '../../utils/achievements';

function ProgressRing({ progress, target }: { progress: number; target: number }) {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const ratio = Math.min(progress / target, 1);
  const offset = circumference * (1 - ratio);

  return (
    <div className="absolute -inset-0.5 rounded-full">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 44 44">
        <circle cx="22" cy="22" r={radius} fill="none" strokeWidth={2}
          className="stroke-slate-700/15" />
        <circle cx="22" cy="22" r={radius} fill="none" strokeWidth={2}
          strokeLinecap="round"
          className="stroke-accent/35 transition-all duration-600"
          strokeDasharray={circumference}
          strokeDashoffset={offset} />
      </svg>
    </div>
  );
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return n.toString();
}

export function AchievementBadge({ achievement, onClick }: { achievement: Achievement; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center gap-1.5 p-2.5 pb-2 rounded-xl border text-center transition-all active:scale-95 ${
        achievement.unlocked
          ? 'border-accent/25 bg-accent/[0.06]'
          : 'border-slate-700/25 bg-slate-800/60'
      }`}
      style={{ minWidth: 80 }}
    >
      <div className="relative">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center text-[22px] leading-none ${
            achievement.unlocked
              ? 'bg-gradient-to-br from-accent/20 to-accent/8 border-[1.5px] border-accent/30 shadow-[0_0_12px_rgba(var(--color-accent),0.1)]'
              : 'bg-slate-800/80 border-[1.5px] border-slate-700/30 saturate-[0.25] opacity-50'
          }`}
        >
          <span role="img">{achievement.icon}</span>
        </div>
        {achievement.unlocked && (
          <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-accent rounded-full flex items-center justify-center border-2 border-slate-800">
            <Check size={8} className="text-slate-900" strokeWidth={3} />
          </div>
        )}
        {!achievement.unlocked && achievement.target != null && achievement.progress != null && (
          <ProgressRing progress={achievement.progress} target={achievement.target} />
        )}
      </div>
      <span className={`text-[10.5px] font-semibold leading-tight min-h-[26px] flex items-center ${
        achievement.unlocked ? 'text-slate-100' : 'text-slate-400'
      }`}>
        {achievement.title}
      </span>
      {!achievement.unlocked && achievement.target != null && achievement.progress != null && (
        <span className="text-[9px] font-medium text-slate-500 -mt-0.5">
          {formatCompact(achievement.progress)}/{formatCompact(achievement.target)}
        </span>
      )}
    </button>
  );
}
